import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import AuthAPI from '../api/auth';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { userCanReadResource } from './permissionsHelper';
import { playTaskPopupSound, setTaskNotificationTone } from './taskPopupSound';

// Visão consolidada do chat interno (#1752). O `pubsub_token` é do USUÁRIO: tudo o que o servidor
// difunde para a pessoa — de qualquer conta dela — chega neste mesmo stream. O predicado da conta
// (`isAValidEvent`) descarta o que não é da conta da URL, e a ÚNICA exceção é esta lista, escrita
// por extenso: os eventos do chat interno, e só para conta que o servidor listou em
// `GET /internal_chat/accounts` (`internalChat/isCrossAccountEligible`).
//
// Lista fechada, e não um prefixo `internal_chat.`: evento novo do chat não atravessa contas por
// acidente — alguém tem de pô-lo aqui, e o spec (actionCableInternalChat.spec.js) confere a lista.
// Nenhum outro evento (mensagem de cliente, notificação, presença, conversa) passa de outra conta.
export const CROSS_ACCOUNT_EVENTS = Object.freeze([
  'internal_chat.message_created',
  'internal_chat.message_updated',
  'internal_chat.channel_read',
  'internal_chat.channel_updated',
]);

// Para a web, a menção do chat interno chega só por `notification.web_created`/`web_updated`
// (#1750): o `notification.created`/`.updated` desse tipo depende do opt-in do celular.
const isWebOnlyNotification = data =>
  data?.notification?.notification_type === 'internal_chat_mention';

// `unread_count`/`count` vêm no recorte do celular; o da web vem em `web_*`.
const withWebCounts = data => ({
  ...data,
  unread_count: data.web_unread_count ?? data.unread_count,
  count: data.web_count ?? data.count,
});

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'company.created': this.onCompanyUpsert,
      'company.updated': this.onCompanyUpsert,
      'company.merged': this.onCompanyUpsert,
      'company.deleted': this.onCompanyDelete,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'notification.web_created': this.onNotificationWebCreated,
      'notification.web_updated': this.onNotificationWebUpdated,
      'first.reply.created': this.onFirstReplyCreated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      'campaign.updated': this.onCampaignUpdated,
      'inbox.updated': this.onInboxUpdated,
      'billing.updated': this.onBillingUpdated,
      'internal_chat.message_created': this.onInternalChatMessageCreated,
      'internal_chat.channel_read': this.onInternalChatChannelRead,
      'internal_chat.channel_updated': this.onInternalChatChannelUpdated,
      'internal_chat.message_updated': this.onInternalChatMessageUpdated,
      'flip_leads.revelacao_atualizada': this.onFlipLeadsRevelacaoUpdated,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = (data, event) => {
    const { getters } = this.app.$store;
    if (getters.getCurrentAccountId === data.account_id) return true;
    if (!CROSS_ACCOUNT_EVENTS.includes(event)) return false;
    return !!getters['internalChat/isCrossAccountEligible']?.(data.account_id);
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
      this.app.$store.dispatch(
        'contactConversations/updateConversation',
        payload
      );
    }
  };

  onAssigneeChanged = payload => {
    if (this.ignoreSandboxEvent(payload)) return;
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
      this.app.$store.dispatch(
        'contactConversations/updateConversation',
        payload
      );
    }
    this.fetchConversationStats();
  };

  // Conversa de sandbox não entra nas listas pelo socket — espelho do corte
  // das listagens HTTP, senão ela aparece ao vivo e some no F5. Se JÁ está no
  // store (aberta pelo link do painel de teste ou do log de execuções), o
  // evento segue passando; sem isso a conversa de teste aberta congelaria.
  ignoreSandboxEvent = data => {
    if (!data?.sandbox) return false;
    return !this.app.$store.getters.getConversationById(data.id);
  };

  onConversationCreated = data => {
    if (this.ignoreSandboxEvent(data)) return;
    this.app.$store.dispatch('addConversation', data);
    this.app.$store.dispatch('contactConversations/updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationRead = data => {
    if (this.ignoreSandboxEvent(data)) return;
    this.app.$store.dispatch('updateConversation', data);
    this.app.$store.dispatch('contactConversations/updateConversation', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('contactConversations/addMessage', data);
    if (lastActivityAt && conversationId) {
      this.app.$store.dispatch('updateConversationLastActivity', {
        lastActivityAt,
        conversationId,
      });
    }
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onStatusChange = data => {
    if (this.ignoreSandboxEvent(data)) return;
    this.app.$store.dispatch('updateConversation', data);
    this.app.$store.dispatch('contactConversations/updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationUpdated = data => {
    if (this.ignoreSandboxEvent(data)) return;
    this.app.$store.dispatch('updateConversation', data);
    this.app.$store.dispatch('contactConversations/updateConversation', data);
    this.fetchConversationStats();
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = () => {
    emitter.emit('fetch_conversation_stats');
    emitter.emit('fetch_overview_reports');
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
    if (Object.prototype.hasOwnProperty.call(data, 'labels')) {
      const labelTitles = Array.isArray(data.labels)
        ? data.labels
            .map(label =>
              typeof label === 'string' ? label : label?.title || label?.name
            )
            .filter(Boolean)
        : [];

      this.app.$store.dispatch('contactLabels/setContactLabel', {
        id: data.id,
        data: labelTitles,
      });
    }
  };

  onCompanyUpsert = data => {
    this.app.$store.commit('companies/SET_COMPANY_ITEM', data);
  };

  onCompanyDelete = data => {
    this.app.$store.commit('companies/DELETE_COMPANY', data.id);
  };

  onNotificationCreated = data => {
    if (isWebOnlyNotification(data)) return;
    this.app.$store.dispatch('notifications/addNotification', {
      notification: data,
      unread_count:
        this.app.$store.getters['notifications/getMeta']?.unreadCount || 0,
      count: this.app.$store.getters['notifications/getMeta']?.count || 0,
    });
    this.maybePlayTaskPopupSound(data);
  };

  maybePlayTaskPopupSound = data => {
    const { notification_type: notificationType, id } = data || {};
    if (!['task_due_today', 'task_overdue'].includes(notificationType)) return;

    const uiSettings = this.app.$store.getters.getUISettings || {};
    if (uiSettings.task_popup_sound_enabled === false) return;

    setTaskNotificationTone(uiSettings.task_popup_sound_tone || 'flip');
    playTaskPopupSound(id);
  };

  // O payload já vem no formato {notification, unread_count, count} da web.
  onNotificationWebCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
  };

  onNotificationWebUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch(
      'notifications/deleteNotification',
      withWebCounts(data)
    );
  };

  onNotificationUpdated = data => {
    if (isWebOnlyNotification(data)) return;
    this.app.$store.dispatch(
      'notifications/updateNotification',
      withWebCounts(data)
    );
  };

  // eslint-disable-next-line class-methods-use-this
  onFirstReplyCreated = () => {
    emitter.emit('fetch_overview_reports');
  };

  onCampaignUpdated = data => {
    this.app.$store.dispatch('campaigns/updateCampaign', data);
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    const { getters } = this.app.$store;
    // O evento vai para todo atendente da conta a cada etiqueta alterada; sem
    // permissao de ler etiquetas o GET da revalidacao so devolvia 403 (#1625).
    if (
      userCanReadResource(
        'labels',
        getters.getCurrentUser,
        getters.getCurrentAccountId
      )
    ) {
      this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    }
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };

  onInboxUpdated = data => {
    // `EDIT_INBOXES` SUBSTITUI o registro inteiro. O payload do socket e o recorte publico da
    // caixa — sem provider_config, IMAP/SMTP, hmac_token nem instance_name —, entao substituir
    // apagava do store local do administrador exatamente os campos que a tela de configuracao
    // dele usa. `MERGE_INBOXES` mescla, e nao insere: `inbox.updated` vai para todos os
    // atendentes da conta, inclusive quem nao e membro da caixa.
    this.app.$store.commit('inboxes/MERGE_INBOXES', data);
  };

  onBillingUpdated = data => {
    emitter.emit(BUS_EVENTS.BILLING_UPDATED, data);
  };

  // Chat interno (#1748). O servidor só manda para quem participa do canal e ainda pode usar o
  // chat naquela conta; a conta conferida aqui é a da tela, ou uma da visão consolidada (#1752,
  // `isAValidEvent` + CROSS_ACCOUNT_EVENTS). Quem decide se a mensagem conta como não lida é o
  // store — ver internalChat.js.
  onInternalChatMessageCreated = data => {
    this.app.$store.dispatch('internalChat/receiveMessage', data);
    this.emitInternalChatMention(data);
  };

  // O aviso "Fulano mencionou você" (#1784). Só sai quando o evento passou a citar alguém
  // (`new_mention_user_ids`); se é o usuário logado, e se cabe avisar, decide o MentionToasts.vue.
  // eslint-disable-next-line class-methods-use-this
  emitInternalChatMention = data => {
    if (!data?.new_mention_user_ids?.length) return;
    emitter.emit(BUS_EVENTS.INTERNAL_CHAT_MENTIONED, data);
  };

  onInternalChatChannelRead = data => {
    this.app.$store.dispatch('internalChat/receiveChannelRead', data);
  };

  // Grupo criado, membro que entrou ou saiu, grupo desfeito (#1749).
  onInternalChatChannelUpdated = data => {
    this.app.$store.dispatch('internalChat/receiveChannelUpdated', data);
  };

  // Mensagem editada ou apagada (#1757). A apagada vem sem conteúdo.
  onInternalChatMessageUpdated = data => {
    this.app.$store.dispatch('internalChat/receiveMessageUpdated', data);
    this.emitInternalChatMention(data);
  };

  // O celular revelado pela Apollo (#1514). O broadcast vai SÓ para quem clicou — o payload carrega
  // celular e e-mail, e mandá-lo para a conta inteira entregaria dado pessoal pago a quem não tem a
  // permissão de revelar, fora de qualquer policy (o socket não passa por Pundit).
  //
  // Vai por `emitter` e não por `this.app.$store` como a maioria dos handlers daqui: quem precisa do
  // evento é UMA tela — a ficha da empresa que está aberta esperando —, e pôr no store obrigaria o
  // estado a sobreviver à navegação sem ninguém para lê-lo. Daí a regra abaixo: este método não usa
  // `this` por construção, como o `onBillingUpdated`.
  // eslint-disable-next-line class-methods-use-this
  onFlipLeadsRevelacaoUpdated = data => {
    emitter.emit(BUS_EVENTS.FLIP_LEADS_REVELACAO_UPDATED, data);
  };
}

export default {
  init(pubsubToken) {
    return new ActionCableConnector(window.WOOT, pubsubToken);
  },
};
